import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { CoreConfigService } from "../../../../@core/services/config.service";
import { UtilsService } from "../../../auth/service/utils.service";
import { ActivatedRoute, Router } from "@angular/router";
import { UserService } from "../../../auth/service";

@Component({
  selector: "app-change-password",
  templateUrl: "./change-password.component.html",
  styleUrls: ["./change-password.component.scss"],
})
export class ChangePasswordComponent implements OnInit {
  // Public
  public coreConfig: any;
  public passwordTextType: boolean;
  public confPasswordTextType: boolean;
  public resetPasswordForm: FormGroup;
  public submitted = false;
  public currentUrl = "";
  public isCreatePasswordPage = false;
  public currentUser: any;
  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {CoreConfigService} _coreConfigService
   * @param {FormBuilder} _formBuilder
   * @param _utilService
   * @param router
   * @param _userService
   * @param _activatedRoute
   */
  constructor(
    private _coreConfigService: CoreConfigService,
    private _formBuilder: FormBuilder,
    private _utilService: UtilsService,
    private router: Router,
    private _userService: UserService,
    private _activatedRoute: ActivatedRoute,
  ) {
    this._unsubscribeAll = new Subject();

    // Configure the layout
    this._coreConfigService.config = {
      layout: {
        navbar: {
          hidden: true,
        },
        menu: {
          hidden: true,
        },
        footer: {
          hidden: true,
        },
        customizer: false,
        enableLocalStorage: false,
      },
    };
    // Subscribe to config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
      this.coreConfig = config;
    });
    this._activatedRoute.queryParams.subscribe((params) => {
      if (params.token && params.u) {
        localStorage.clear();
        localStorage.setItem("token", params.token);
        localStorage.setItem("user", params.u);
        this.router.navigateByUrl(window.location.pathname);
      }
    });
    this.resetPasswordForm = this._formBuilder.group({
      newPassword: ["", [Validators.required]],
      confirmPassword: ["", [Validators.required]],
    });
    this.currentUrl = this.router.url;
    this.isCreatePasswordPage = this.currentUrl === "/landing/create-password";
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  async ngOnInit() {
    const hash: string = localStorage.getItem("user");
    if (hash) {
      this.currentUser = await this._userService.get(hash);
      if (!this.currentUser) {
        await this.router.navigateByUrl("/");
      }
    }
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.resetPasswordForm.controls;
  }

  /**
   * Toggle password
   */
  togglePasswordTextType() {
    this.passwordTextType = !this.passwordTextType;
  }

  /**
   * Toggle confirm password
   */
  toggleConfPasswordTextType() {
    this.confPasswordTextType = !this.confPasswordTextType;
  }

  /**
   * On Submit
   */
  async onSubmit() {
    this.submitted = true;

    // stop here if form is invalid
    if (this.resetPasswordForm.invalid) {
      return;
    }
    //check if password are identical
    if (this.resetPasswordForm.value.newPassword !== this.resetPasswordForm.value.confirmPassword) {
      await this._utilService.showSWALError({ text: "ERROR_NOT_IDENTICAL_PASSWORD" });
      return false;
    }
    if (this.resetPasswordForm.value.newPassword && this.resetPasswordForm.value.newPassword.length < 6) {
      await this._utilService.showSWALError({ text: "MINIMUM_CHARACTERS_PASSWORD_ERROR" });
      return false;
    }
    this._utilService.showLoading();
    //const passwordToSend = this._utilService.encryptData(this.resetPasswordForm.value.newPassword);
    await this._userService
      .sendPassword({ password: this.resetPasswordForm.value.newPassword })
      .then(async () => {
        this._utilService.hideLoading();
        await this._utilService.showSWALSuccess({ text: this.isCreatePasswordPage ? "SUCCESS_CREATE_PASSWORD" : "SUCCESS_EDIT_PASSWORD" });
        localStorage.clear();
        await this.router.navigateByUrl("/auth/login");
      })
      .catch(async () => {
        this._utilService.hideLoading();
        await this._utilService.showSWALError({ text: "ERROR_CREATE_PASSWORD" });
      });
  }
}
